import { Component, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { ITransaction } from 'core/types/transaction.interface';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TransactionTypeIds } from 'core/types/transaction.interface';
import { IKorviuListHighlightedItem } from 'shared/types/korviu-list.config';
import { formatBalanceDate } from 'shared/utils/balance-format.utils';

type TransactionAmountColorClass = 'green' | 'red' | '';

export function formatTransactionDisplayDate(
  transaction:
    | Partial<Pick<ITransaction, 'id' | 'earned_month' | 'event_date' | 'timestamp' | 'timestamp_display'>>
    | null
    | undefined,
  language = 'en'
): string {
  const earnedMonth = transaction?.id === null ? formatEarnedMonth(transaction.earned_month, language) : '';

  if (earnedMonth) {
    return earnedMonth;
  }

  const timestampDisplay = transaction?.timestamp_display?.trim();

  if (timestampDisplay) {
    return timestampDisplay;
  }

  // The API's business/file date is a calendar date, independent of the browser timezone.
  // Preserve the ISO calendar date on legacy rows that lack both canonical date fields.
  return formatBalanceDate(transaction?.event_date, language) || formatBalanceDate(transaction?.timestamp, language);
}

function formatEarnedMonth(earnedMonth: string | null | undefined, language: string): string {
  const match = earnedMonth?.trim().match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);

  if (!match) {
    return '';
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;

  if (monthIndex < 0 || monthIndex > 11) {
    return '';
  }

  return new Intl.DateTimeFormat(language.toLowerCase().startsWith('es') ? 'es-ES' : 'en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

export function getTransactionAmountColorClass(
  transaction: Partial<Pick<ITransaction, 'amount'>> | null | undefined
): TransactionAmountColorClass {
  const amount = Number(transaction?.amount);

  if (!Number.isFinite(amount) || amount === 0) {
    return '';
  }

  return amount > 0 ? 'green' : 'red';
}

export function isAggregateDeposit(
  transaction: Pick<ITransaction, 'id' | 'transaction_type'> | null | undefined
): boolean {
  return (
    transaction?.id === null &&
    (transaction.transaction_type?.id === TransactionTypeIds.DEPOSIT ||
      transaction.transaction_type?.name?.toLowerCase() === 'deposit')
  );
}

export function canOpenTransactionDetails(
  transaction: Pick<ITransaction, 'id' | 'transaction_type'> | null | undefined
): boolean {
  return typeof transaction?.id === 'number' || isAggregateDeposit(transaction);
}

export function getTransactionTrackKey(
  transaction: Pick<ITransaction, 'id' | 'source' | 'earned_month' | 'event_date' | 'timestamp_display'>
): string {
  if (typeof transaction.id === 'number') {
    return `${transaction.source}:${transaction.id}`;
  }

  return `aggregate:${transaction.earned_month || transaction.event_date || transaction.timestamp_display}`;
}

@Component({
  selector: 'transaction-list',
  standalone: true,
  imports: [NzIconModule, TranslateModule],
  templateUrl: './transaction-list.component.html',
  styleUrl: './transaction-list.component.scss'
})
export class TransactionListComponent {
  #translateService = inject(TranslateService);

  readonly highlightedItem = input<IKorviuListHighlightedItem>();
  readonly detailsEnabled = input(true);

  @Input() public transactions: ITransaction[];
  @Output() public recordClick: EventEmitter<ITransaction>;

  get currentLang(): string {
    return this.#translateService.currentLang;
  }

  constructor() {
    this.recordClick = new EventEmitter<ITransaction>();
  }

  public onRecordClick(record: ITransaction): void {
    if (!this.canOpenDetails(record)) {
      return;
    }

    this.recordClick.emit(record);
  }

  public canOpenDetails(transaction: ITransaction): boolean {
    return this.detailsEnabled() && canOpenTransactionDetails(transaction);
  }

  public getTrackKey(transaction: ITransaction): string {
    return getTransactionTrackKey(transaction);
  }

  public isHighlighted(transaction: ITransaction): boolean {
    const highlightedItem = this.highlightedItem();

    if (!highlightedItem) {
      return false;
    }

    switch (highlightedItem.field) {
      case 'id':
        return transaction.id === highlightedItem.value;
      case 'purchaseId': {
        const purchaseId = typeof transaction.purchase === 'number' ? transaction.purchase : transaction.purchase?.id;
        return purchaseId === highlightedItem.value;
      }
      case 'earnedMonth':
        return transaction.earned_month === highlightedItem.value;
    }
  }

  public isPurchase(transaction: ITransaction): boolean {
    return transaction?.transaction_type?.id === TransactionTypeIds.PURCHASE;
  }

  public isTransfer(transaction: ITransaction): boolean {
    return transaction?.transaction_type?.id === TransactionTypeIds.TRANSFER;
  }

  public isDeposit(transaction: ITransaction): boolean {
    return (
      transaction?.transaction_type?.id === TransactionTypeIds.DEPOSIT ||
      transaction?.transaction_type?.name?.toLowerCase() === 'deposit'
    );
  }

  public isReimburse(transaction: ITransaction): boolean {
    return transaction?.transaction_type?.id === TransactionTypeIds.REIMBURSE;
  }

  public isTransactionAmountCredit(transaction: ITransaction): boolean {
    return getTransactionAmountColorClass(transaction) === 'green';
  }

  public isTransactionAmountDebit(transaction: ITransaction): boolean {
    return getTransactionAmountColorClass(transaction) === 'red';
  }

  public isAmountPositive(transaction: ITransaction): boolean {
    return +transaction.amount > 0;
  }

  public isAmountNegative(transaction: ITransaction): boolean {
    return +transaction.amount < 0;
  }

  public getTransactionDisplayDate(transaction: ITransaction): string {
    return formatTransactionDisplayDate(transaction, this.currentLang);
  }
}
